import { getProgramReportById } from '../../products/common/services/program-service';
import { modalUtils } from '@/app/core/utils';
import {
  Job,
  Observation,
  fetchAndEnforceApiGatewayResponseData,
} from '@ssar-app/domain';
import {
  Alert,
  Descriptions,
  Empty,
  Modal,
  SummaryCard,
  Tabs,
} from '@ssar-app/uikit';
import {
  Flex,
  Icon,
  List,
  ListItem,
  Progress,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { AiOutlineFileText } from 'react-icons/ai/index.js';

interface ButtonProps {
  record: Job;
  setIsLoading: (arg0: boolean) => void;
}

const ObservationFC = (props: Observation) => {
  return (
    <SummaryCard
      title={`${props.errorColumn} - Failed row numbers`}
      titleProps={{
        borderBottom: '1px solid',
        borderColor: 'gray.300',
        pb: 4,
        fontSize: 'md',
      }}
      secondaryTitle={
        <>
          <Descriptions size='small' column={10} bordered={false}>
            {props.rowNumbers.map((a, index) => (
              <Descriptions.Item key={`${a}_${index}`}>
                <Text color='red.500'>{a}</Text>
              </Descriptions.Item>
            ))}
          </Descriptions>
          <br></br>
          <Text color='gray.500'>{props.errorDetails}</Text>
        </>
      }
      secondaryTitleProps={{ mt: 4, whiteSpace: 'unset' }}
      variant='ghost'
    />
  );
};

export const JobsReportButton = (props: ButtonProps) => {
  const { record, setIsLoading } = props;
  // const [isDownloading, setIsDownloading] = useState(false);

  interface PercentageOfColumns {
    columnName: string;
    percentage: number;
  }

  interface ColumnsCount {
    title: string;
    count: number;
    type: 'success' | 'info' | 'warning' | 'error' | undefined;
  }

  const showReport = (): void => {
    fetchAndEnforceApiGatewayResponseData(() =>
      getProgramReportById(record.externalSystemRef),
    )
      .then((reportData) => {
        if (reportData) {
          const modal = Modal.info({});
          modal.update({
            visible: true,
            showDivider: true,
            closable: true,
            icon: false,
            header: 'Validation report',
            contentProps: { maxW: '80%' },
            headerProps: { color: 'black', fontWeight: 'light' },
            showCancel: false,
            onCancel: () => modal.destroy(),
            onOk: () => modal.destroy(),
            okButtonProps: { size: 'sm', pl: 4, pr: 4 },
            bodyProps: { pb: 0 },
            content: (
              <>
                <Alert
                  title={`Validation ${reportData.validationStatus}`}
                  description={reportData.message}
                  type={
                    reportData.validationStatus === 'Success'
                      ? 'success'
                      : 'error'
                  }
                  isOpen
                  showCloseButton={false}
                  showIcon
                />
                <br></br>
                {reportData.totalRecords > 0 && (
                  <>
                    <SummaryCard
                      title='Records'
                      titleProps={{
                        borderBottom: '1px solid',
                        borderColor: 'gray.300',
                        pb: 4,
                        fontSize: 'md',
                      }}
                      secondaryTitle={
                        <List
                          display='grid'
                          gap={4}
                          gridTemplateColumns='repeat(3,1fr)'
                        >
                          {getDataListArray(
                            reportData.totalRecords,
                            reportData.validationPassed,
                            reportData.validationFailed,
                          ).map((item, index) => (
                            <ListItem key={index}>
                              <Alert
                                title={item.title}
                                description={item.count.toString()}
                                type={item.type || 'info'}
                                isOpen
                                showCloseButton={false}
                                showIcon
                              />
                            </ListItem>
                          ))}
                        </List>
                      }
                      secondaryTitleProps={{ mt: 4, whiteSpace: 'unset' }}
                      variant='ghost'
                    />
                    <SummaryCard
                      title='Success percentage of columns'
                      titleProps={{
                        borderBottom: '1px solid',
                        borderColor: 'gray.300',
                        pb: 4,
                        fontSize: 'md',
                      }}
                      secondaryTitle={
                        <List
                          display='grid'
                          gap={4}
                          gridTemplateColumns='repeat(3,1fr)'
                        >
                          {getPercentageOfColumnsArray(
                            reportData.percentageOfColumns,
                          ).map((item, index) => (
                            <ListItem key={index}>
                              <Text>{item.columnName}</Text>
                              <Flex alignItems='center' gap={2}>
                                <Progress
                                  value={item.percentage}
                                  size='sm'
                                  colorScheme={
                                    item.percentage === 100 ? 'green' : 'orange'
                                  }
                                  bg='gray.300'
                                  w='full'
                                  borderRadius='full'
                                />
                                <Text fontWeight='bold' fontSize='xs'>
                                  {item.percentage}%
                                </Text>
                              </Flex>
                            </ListItem>
                          ))}
                        </List>
                      }
                      secondaryTitleProps={{ mt: 4, whiteSpace: 'unset' }}
                      variant='ghost'
                    />
                  </>
                )}
                {reportData.observations.length > 0 && (
                  <SummaryCard
                    title={`Failed column details with ${reportData.sampleRowsMaxLimit} sample row numbers`}
                    titleProps={{
                      borderBottom: '1px solid',
                      borderColor: 'gray.300',
                      pb: 4,
                      fontSize: 'md',
                    }}
                    secondaryTitle={
                      <Tabs
                        items={reportData.observations.map(
                          (observation, index) => ({
                            label: (
                              <>
                                {observation.errorColumn} &nbsp;
                                <Text color='red.500'>
                                  {observation.totalErrorCount}
                                </Text>
                              </>
                            ),
                            key: `${observation.errorColumn}_${index}`,
                            content: <ObservationFC {...observation} />,
                          }),
                        )}
                        variant='line'
                        tabListProps={{ borderColor: 'gray.500' }}
                        tabProps={{ height: 'fit-content' }}
                        tabPanelProps={{ padding: 0 }}
                        tabPosition='left'
                      />
                    }
                    secondaryTitleProps={{ mt: 4, whiteSpace: 'unset' }}
                    variant='ghost'
                  />
                )}
                {reportData.totalRecords === 0 &&
                  reportData.observations.length === 0 && (
                    <Empty description='Report Unavailable' />
                  )}
              </>
            ),
          });
        } else {
          modalUtils.showError('Invalid Report');
        }
      })
      .catch((err) => {
        modalUtils.showError(err, 'Error while fetching the Program details');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // const downloadClicked = (): void => {
  //   setIsDownloading(true);
  //   getProgramReportById(record.programId)
  //     .then((data) => {
  //       setReportData(data.data);
  //       setIsDownloading(false);
  //       console.log(data);
  //     })
  //     .catch((err) => {
  //       modalUtils.showError(err, 'Error while fetching the Program details');
  //     })
  //     .finally(() => {
  //       setIsDownloading(false);
  //     });
  // };

  const getPercentageOfColumnsArray = (
    percentageOfColumns: Record<string, number>,
  ): PercentageOfColumns[] => {
    const toReturn: PercentageOfColumns[] = [];
    for (const column in percentageOfColumns) {
      toReturn.push({
        columnName: column,
        percentage: percentageOfColumns[column],
      });
    }
    return toReturn;
  };

  const getDataListArray = (
    total: number,
    passed: number,
    failed: number,
  ): ColumnsCount[] => {
    const toReturn: ColumnsCount[] = [];
    toReturn.push({
      title: 'Total',
      count: total,
      type: 'warning',
    });
    toReturn.push({
      title: 'Passed',
      count: passed,
      type: 'success',
    });
    toReturn.push({
      title: 'Failed',
      count: failed,
      type: 'error',
    });
    return toReturn;
  };

  return (
    <Tooltip
      placement='left'
      label='Show backend file validation report'
      hasArrow
    >
      <a
        href='/show-validation-report'
        onClick={(e) => {
          e.preventDefault();
          setIsLoading(true);
          showReport();
        }}
      >
        <Icon as={AiOutlineFileText} /> Validation Report
      </a>
    </Tooltip>
  );
};
